// src/projects/project_finances/ReceiptCropper.js
// Document-scanner crop step for receipt photos.
//
// ZERO external dependencies — no OpenCV, no WASM, no downloads. The
// perspective correction ("de-warp") is computed directly in plain JS:
// a 4-point homography solved with Gaussian elimination, applied with
// bilinear sampling. On a ≤1280px working image this takes well under a
// second on any phone, and there is nothing that can hang, freeze, or
// fail to download. Corners start as a rectangle and are dragged manually.
//
// Fully self-contained: delete this file + its <ReceiptCropper> usage and
// nothing else breaks.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Check, X, Crop, RotateCcw, RotateCw, Maximize } from 'lucide-react';

// Rotate a canvas 90° clockwise (returns a new canvas).
function rotate90(canvas) {
  const out = document.createElement('canvas');
  out.width = canvas.height;
  out.height = canvas.width;
  const ctx = out.getContext('2d');
  ctx.translate(out.width / 2, out.height / 2);
  ctx.rotate(Math.PI / 2);
  ctx.drawImage(canvas, -canvas.width / 2, -canvas.height / 2);
  return out;
}

const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

// Order arbitrary 4 points as [topLeft, topRight, bottomRight, bottomLeft]
function orderCorners(pts) {
  const bySum = [...pts].sort((a, b) => (a.x + a.y) - (b.x + b.y));
  const tl = bySum[0], br = bySum[3];
  const byDiff = [...pts].sort((a, b) => (a.x - a.y) - (b.x - b.y));
  const bl = byDiff[0], tr = byDiff[3];
  return [tl, tr, br, bl];
}

// Solve the 3x3 homography mapping 4 src points onto 4 dst points
// (direct linear transform + Gaussian elimination, h[8] fixed at 1).
function computeHomography(src, dst) {
  const A = [], b = [];
  for (let i = 0; i < 4; i++) {
    const { x, y } = src[i];
    const { x: X, y: Y } = dst[i];
    A.push([x, y, 1, 0, 0, 0, -X * x, -X * y]); b.push(X);
    A.push([0, 0, 0, x, y, 1, -Y * x, -Y * y]); b.push(Y);
  }
  for (let col = 0; col < 8; col++) {
    let piv = col;
    for (let r = col + 1; r < 8; r++) if (Math.abs(A[r][col]) > Math.abs(A[piv][col])) piv = r;
    [A[col], A[piv]] = [A[piv], A[col]];
    [b[col], b[piv]] = [b[piv], b[col]];
    const d = A[col][col] || 1e-12;
    for (let r = col + 1; r < 8; r++) {
      const f = A[r][col] / d;
      for (let c = col; c < 8; c++) A[r][c] -= f * A[col][c];
      b[r] -= f * b[col];
    }
  }
  const h = new Array(8);
  for (let r = 7; r >= 0; r--) {
    let s = b[r];
    for (let c = r + 1; c < 8; c++) s -= A[r][c] * h[c];
    h[r] = s / (A[r][r] || 1e-12);
  }
  return [...h, 1];
}

// Perspective-correct the quadrilateral into a flat rectangle (pure JS,
// bilinear sampling). corners = [tl, tr, br, bl] in source-canvas coords.
function warpToRect(canvas, corners) {
  const [tl, tr, br, bl] = corners;
  const W = Math.max(32, Math.round(Math.max(dist(tl, tr), dist(bl, br))));
  const H = Math.max(32, Math.round(Math.max(dist(tl, bl), dist(tr, br))));

  // Homography maps OUTPUT pixel coords -> SOURCE coords
  const h = computeHomography(
    [{ x: 0, y: 0 }, { x: W, y: 0 }, { x: W, y: H }, { x: 0, y: H }],
    [tl, tr, br, bl]
  );

  const srcCtx = canvas.getContext('2d');
  const srcData = srcCtx.getImageData(0, 0, canvas.width, canvas.height);
  const sPix = srcData.data, sw = canvas.width, sh = canvas.height;

  const out = document.createElement('canvas');
  out.width = W; out.height = H;
  const outCtx = out.getContext('2d');
  const outData = outCtx.createImageData(W, H);
  const oPix = outData.data;

  for (let v = 0; v < H; v++) {
    for (let u = 0; u < W; u++) {
      const w = h[6] * u + h[7] * v + 1;
      const sx = (h[0] * u + h[1] * v + h[2]) / w;
      const sy = (h[3] * u + h[4] * v + h[5]) / w;
      const oi = (v * W + u) * 4;

      if (sx < 0 || sy < 0 || sx > sw - 1 || sy > sh - 1) {
        oPix[oi] = oPix[oi + 1] = oPix[oi + 2] = 255; oPix[oi + 3] = 255; // white outside
        continue;
      }
      // bilinear sample
      const x0 = sx | 0, y0 = sy | 0;
      const x1 = Math.min(x0 + 1, sw - 1), y1 = Math.min(y0 + 1, sh - 1);
      const fx = sx - x0, fy = sy - y0;
      const i00 = (y0 * sw + x0) * 4, i10 = (y0 * sw + x1) * 4;
      const i01 = (y1 * sw + x0) * 4, i11 = (y1 * sw + x1) * 4;
      for (let c = 0; c < 3; c++) {
        const top = sPix[i00 + c] * (1 - fx) + sPix[i10 + c] * fx;
        const bot = sPix[i01 + c] * (1 - fx) + sPix[i11 + c] * fx;
        oPix[oi + c] = top * (1 - fy) + bot * fy;
      }
      oPix[oi + 3] = 255;
    }
  }
  outCtx.putImageData(outData, 0, 0);
  return out;
}

// ---- AUTO-CROP: detect the document's four corners (pure JS, no deps) ----
// Downscale → grayscale → Otsu threshold → largest bright region (the paper)
// → take its sum/difference extremes as the four corners. This handles moderate
// skew/perspective. It is confidence-gated: if the receipt doesn't stand out
// from the background (white-on-white, heavy shadow, or it fills the whole
// frame), it returns null and we fall back to the default rectangle — so the
// worst case is exactly today's manual experience, never a bad auto-crop.
// Returns corners [tl, tr, br, bl] in WORK-canvas coords, or null.
function detectDocumentQuad(work) {
  try {
    const MAXD = 320;
    const scale = Math.min(1, MAXD / Math.max(work.width, work.height));
    const w = Math.max(1, Math.round(work.width * scale));
    const h = Math.max(1, Math.round(work.height * scale));
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    const ctx = c.getContext('2d');
    ctx.drawImage(work, 0, 0, w, h);
    const data = ctx.getImageData(0, 0, w, h).data;

    const gray = new Float32Array(w * h);
    for (let i = 0, p = 0; i < gray.length; i++, p += 4) {
      gray[i] = 0.299 * data[p] + 0.587 * data[p + 1] + 0.114 * data[p + 2];
    }

    // Otsu threshold
    const hist = new Array(256).fill(0);
    for (let i = 0; i < gray.length; i++) hist[gray[i] | 0]++;
    const total = gray.length;
    let sumAll = 0;
    for (let t = 0; t < 256; t++) sumAll += t * hist[t];
    let sumB = 0, wB = 0, maxVar = -1, thr = 127;
    for (let t = 0; t < 256; t++) {
      wB += hist[t]; if (!wB) continue;
      const wF = total - wB; if (!wF) break;
      sumB += t * hist[t];
      const mB = sumB / wB, mF = (sumAll - sumB) / wF;
      const v = wB * wF * (mB - mF) * (mB - mF);
      if (v > maxVar) { maxVar = v; thr = t; }
    }

    // Foreground = brighter than threshold (paper is usually lighter than desk).
    const fg = new Uint8Array(w * h);
    for (let i = 0; i < gray.length; i++) fg[i] = gray[i] > thr ? 1 : 0;

    // Largest 4-connected bright component.
    const label = new Int32Array(w * h);
    let best = null;
    const stack = [];
    let cur = 0;
    for (let i = 0; i < fg.length; i++) {
      if (fg[i] && !label[i]) {
        cur++; let size = 0; stack.length = 0; stack.push(i); label[i] = cur;
        const comp = [];
        while (stack.length) {
          const idx = stack.pop(); size++; comp.push(idx);
          const x = idx % w, y = (idx / w) | 0;
          if (x > 0 && fg[idx - 1] && !label[idx - 1]) { label[idx - 1] = cur; stack.push(idx - 1); }
          if (x < w - 1 && fg[idx + 1] && !label[idx + 1]) { label[idx + 1] = cur; stack.push(idx + 1); }
          if (y > 0 && fg[idx - w] && !label[idx - w]) { label[idx - w] = cur; stack.push(idx - w); }
          if (y < h - 1 && fg[idx + w] && !label[idx + w]) { label[idx + w] = cur; stack.push(idx + w); }
        }
        if (!best || size > best.size) best = { size, comp };
      }
    }
    if (!best) return null;

    const area = best.size / (w * h);
    if (area < 0.10 || area > 0.985) return null; // too small, or basically the whole frame

    // Corners = extremes of (x+y) and (x−y) over the component.
    let tl = null, tr = null, br = null, bl = null;
    let sMin = Infinity, sMax = -Infinity, dMin = Infinity, dMax = -Infinity;
    for (const idx of best.comp) {
      const x = idx % w, y = (idx / w) | 0;
      const s = x + y, d = x - y;
      if (s < sMin) { sMin = s; tl = { x, y }; }
      if (s > sMax) { sMax = s; br = { x, y }; }
      if (d < dMin) { dMin = d; bl = { x, y }; }
      if (d > dMax) { dMax = d; tr = { x, y }; }
    }
    if (!tl || !tr || !br || !bl) return null;

    const corners = [tl, tr, br, bl].map(p => ({ x: p.x / scale, y: p.y / scale }));
    // Reject degenerate quads (a side too short = not a real document outline).
    const d2 = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
    const sides = [d2(corners[0], corners[1]), d2(corners[1], corners[2]), d2(corners[2], corners[3]), d2(corners[3], corners[0])];
    const maxDim = Math.max(work.width, work.height);
    if (Math.min(...sides) < maxDim * 0.12) return null;
    return corners;
  } catch (_) {
    return null;
  }
}

const MAX_WORK_SIZE = 1280; // plenty for OCR (backend resizes to 1400 anyway)

function ReceiptCropper({ file, remaining = 1, onDone, onUseOriginal, onCancelAll }) {
  const [stage, setStage] = useState('loading'); // loading | crop | warping | preview
  const [corners, setCorners] = useState(null);  // 4 pts in work-canvas coords
  const workCanvasRef = useRef(null);            // offscreen full image (≤1280px)
  const displayCanvasRef = useRef(null);         // on-screen canvas
  const previewCanvasRef = useRef(null);
  const warpedRef = useRef(null);
  const dragIdx = useRef(-1);
  const loupeCanvasRef = useRef(null);
  const [activeDrag, setActiveDrag] = useState(-1); // which corner is being dragged (for the loupe)
  const [autoDetected, setAutoDetected] = useState(false); // did auto-crop find the edges?
  const defaultCornersRef = useRef(null);           // the inset fallback, for Reset
  const [rotTick, setRotTick] = useState(0);        // bump to repaint after a rotation

  // Rotate the photo 90° clockwise (receipt was scanned sideways). Corners
  // reset — auto-detect re-runs on the rotated image.
  const rotateCropImage = () => {
    if (!workCanvasRef.current) return;
    const rotated = rotate90(workCanvasRef.current);
    workCanvasRef.current = rotated;
    const inset = 0.06;
    const fallback = [
      { x: rotated.width * inset, y: rotated.height * inset },
      { x: rotated.width * (1 - inset), y: rotated.height * inset },
      { x: rotated.width * (1 - inset), y: rotated.height * (1 - inset) },
      { x: rotated.width * inset, y: rotated.height * (1 - inset) },
    ];
    defaultCornersRef.current = fallback;
    const auto = detectDocumentQuad(rotated);
    setAutoDetected(!!auto);
    setCorners(auto || fallback);
    setRotTick(t => t + 1);
  };
  // Rotate the cropped result on the preview screen (caught it late).
  const rotatePreview = () => {
    if (!warpedRef.current) return;
    warpedRef.current = rotate90(warpedRef.current);
    setRotTick(t => t + 1);
  };

  // ---- decode the photo (fast, local) and open the crop UI immediately ----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setStage('loading');
      const url = URL.createObjectURL(file);
      const img = new Image();
      const loaded = new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
      img.src = url;
      try {
        await loaded;
      } catch (_) {
        URL.revokeObjectURL(url);
        if (!cancelled) onUseOriginal(); // undecodable (e.g. HEIC on this browser) — backend handles it
        return;
      }
      if (cancelled) { URL.revokeObjectURL(url); return; }

      const scale = Math.min(1, MAX_WORK_SIZE / Math.max(img.naturalWidth, img.naturalHeight));
      const work = document.createElement('canvas');
      work.width = Math.round(img.naturalWidth * scale);
      work.height = Math.round(img.naturalHeight * scale);
      work.getContext('2d').drawImage(img, 0, 0, work.width, work.height);
      URL.revokeObjectURL(url);
      workCanvasRef.current = work;

      // Try auto-crop first; fall back to a default inset rectangle when the
      // document doesn't stand out enough to detect confidently. Either way the
      // user can fine-tune by dragging the corners.
      const auto = detectDocumentQuad(work);
      const inset = 0.06;
      const fallback = [
        { x: work.width * inset, y: work.height * inset },
        { x: work.width * (1 - inset), y: work.height * inset },
        { x: work.width * (1 - inset), y: work.height * (1 - inset) },
        { x: work.width * inset, y: work.height * (1 - inset) },
      ];
      defaultCornersRef.current = fallback;
      setAutoDetected(!!auto);
      setCorners(auto || fallback);
      setStage('crop');
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  // ---- paint the crop view ----
  useEffect(() => {
    if (stage !== 'crop' || !displayCanvasRef.current || !workCanvasRef.current) return;
    const disp = displayCanvasRef.current;
    const work = workCanvasRef.current;
    disp.width = work.width; disp.height = work.height;
    disp.getContext('2d').drawImage(work, 0, 0);
  }, [stage, rotTick]);

  // ---- paint the preview ----
  useEffect(() => {
    if (stage !== 'preview' || !previewCanvasRef.current || !warpedRef.current) return;
    const disp = previewCanvasRef.current;
    const w = warpedRef.current;
    disp.width = w.width; disp.height = w.height;
    disp.getContext('2d').drawImage(w, 0, 0);
  }, [stage, rotTick]);

  // ---- corner dragging (pointer events: touch + mouse) ----
  const pointFromEvent = useCallback((e) => {
    const rect = displayCanvasRef.current.getBoundingClientRect();
    const work = workCanvasRef.current;
    return {
      x: Math.max(0, Math.min(work.width, ((e.clientX - rect.left) / rect.width) * work.width)),
      y: Math.max(0, Math.min(work.height, ((e.clientY - rect.top) / rect.height) * work.height)),
    };
  }, []);

  const onHandleDown = (i) => (e) => {
    e.preventDefault();
    dragIdx.current = i;
    setActiveDrag(i);
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
  };
  const onHandleMove = (e) => {
    if (dragIdx.current < 0) return;
    const p = pointFromEvent(e);
    setCorners(prev => prev.map((c, i) => (i === dragIdx.current ? p : c)));
  };
  const onHandleUp = () => { dragIdx.current = -1; setActiveDrag(-1); };

  // ---- magnifier loupe: shows what's under the finger while dragging ----
  useEffect(() => {
    if (activeDrag < 0 || !loupeCanvasRef.current || !workCanvasRef.current || !corners) return;
    const work = workCanvasRef.current;
    const c = corners[activeDrag];
    const loupe = loupeCanvasRef.current;
    const SIZE = 110;            // loupe canvas px
    const ZOOM = 2.5;            // magnification
    const srcSize = SIZE / ZOOM; // area of the work canvas shown
    loupe.width = SIZE; loupe.height = SIZE;
    const ctx = loupe.getContext('2d');
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, SIZE, SIZE);
    ctx.drawImage(
      work,
      c.x - srcSize / 2, c.y - srcSize / 2, srcSize, srcSize,
      0, 0, SIZE, SIZE
    );
    // crosshair marking the exact corner position
    ctx.strokeStyle = 'rgba(129,140,248,0.95)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(SIZE / 2, 0); ctx.lineTo(SIZE / 2, SIZE);
    ctx.moveTo(0, SIZE / 2); ctx.lineTo(SIZE, SIZE / 2);
    ctx.stroke();
  }, [activeDrag, corners]);

  // Crop: pure-JS warp, runs locally in well under a second. The tiny
  // timeout lets the spinner paint before the pixel loop runs.
  const doCrop = () => {
    setStage('warping');
    setTimeout(() => {
      try {
        warpedRef.current = warpToRect(workCanvasRef.current, orderCorners(corners));
      } catch (_) {
        warpedRef.current = workCanvasRef.current; // worst case: uncropped
      }
      setStage('preview');
    }, 60);
  };

  const accept = () => {
    // Export at a leaner quality — the backend re-encodes for OCR anyway, so a
    // smaller upload just means faster scans and lighter files (esp. on mobile).
    warpedRef.current.toBlob((blob) => {
      const name = (file.name || 'receipt').replace(/\.\w+$/, '') + '_scan.jpg';
      onDone(new File([blob], name, { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.72);
  };

  // percentage position helpers for handles/outline
  const pct = (c) => workCanvasRef.current
    ? { left: `${(c.x / workCanvasRef.current.width) * 100}%`, top: `${(c.y / workCanvasRef.current.height) * 100}%` }
    : { left: 0, top: 0 };
  const polyPoints = workCanvasRef.current && corners
    ? corners.map(c => `${(c.x / workCanvasRef.current.width) * 100},${(c.y / workCanvasRef.current.height) * 100}`).join(' ')
    : '';

  return (
    <div
      className="fixed inset-0 z-[130] bg-black/90 overflow-y-auto"
      style={{
        // Dragging the crop corners must never select text or flash the blue
        // tap/selection highlight on the image or surrounding UI. Kill all of
        // it for the whole cropper overlay (and its children).
        userSelect: 'none',
        WebkitUserSelect: 'none',
        msUserSelect: 'none',
        WebkitTouchCallout: 'none',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <div className="min-h-full flex flex-col items-center justify-center p-4 max-md:p-2 max-md:pb-8">
      <div className="w-full max-w-2xl flex flex-col items-center">

        {/* Header — always present, always an exit */}
        <div className="w-full flex items-center justify-between mb-3 text-white">
          <p className="font-bold text-sm uppercase tracking-wider">
            {stage === 'preview' ? 'Preview' : 'Adjust Crop'}
            {remaining > 1 && <span className="text-zinc-400 font-medium normal-case"> · {remaining} photos left</span>}
          </p>
          <button onClick={onCancelAll} className="p-2 text-zinc-400 hover:text-white" title="Cancel">
            <X size={22} />
          </button>
        </div>

        {stage === 'loading' && (
          <div className="py-24 text-center text-zinc-300">
            <div className="w-10 h-10 border-2 border-indigo-400/30 border-t-indigo-400 rounded-full animate-spin mx-auto mb-4" />
            Opening photo…
          </div>
        )}

        {stage === 'warping' && (
          <div className="py-24 text-center text-zinc-300">
            <div className="w-10 h-10 border-2 border-indigo-400/30 border-t-indigo-400 rounded-full animate-spin mx-auto mb-4" />
            Straightening…
          </div>
        )}

        {stage === 'crop' && (
          <>
            {/* inline-block wrapper shrink-wraps the canvas so the absolute
                corner handles align exactly with the displayed image */}
            <div className="relative inline-block max-w-full" style={{ touchAction: 'none' }}>
              <canvas
                ref={displayCanvasRef}
                draggable={false}
                className="rounded-lg select-none block"
                style={{ maxWidth: '100%', maxHeight: '52vh', width: 'auto', height: 'auto', WebkitUserSelect: 'none', userSelect: 'none', WebkitTouchCallout: 'none' }}
              />
              {/* outline */}
              {corners && (
                <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
                  <polygon points={polyPoints} fill="rgba(99,102,241,0.15)" stroke="#818cf8" strokeWidth="0.6" vectorEffect="non-scaling-stroke" />
                </svg>
              )}
              {/* draggable corner handles */}
              {corners && corners.map((c, i) => (
                <div
                  key={i}
                  onPointerDown={onHandleDown(i)}
                  onPointerMove={onHandleMove}
                  onPointerUp={onHandleUp}
                  className="absolute w-11 h-11 -ml-[22px] -mt-[22px] flex items-center justify-center select-none"
                  style={{ ...pct(c), touchAction: 'none', cursor: 'grab', WebkitTapHighlightColor: 'transparent', WebkitUserSelect: 'none', userSelect: 'none' }}
                >
                  <div className="w-6 h-6 rounded-full bg-indigo-500 border-[3px] border-white shadow-lg" />
                </div>
              ))}

              {/* magnifier loupe — always floats above the finger so the corner
                  being placed is never hidden under the thumb */}
              {activeDrag >= 0 && corners && workCanvasRef.current && (
                <div
                  className="absolute pointer-events-none rounded-full overflow-hidden border-[3px] border-white shadow-2xl"
                  style={{
                    ...pct(corners[activeDrag]),
                    width: 110,
                    height: 110,
                    // Always float the loupe ABOVE the finger so the corner is
                    // never hidden under the thumb, on every corner.
                    transform: 'translate(-50%, -130%)',
                    zIndex: 20,
                  }}
                >
                  <canvas ref={loupeCanvasRef} className="w-full h-full block" />
                </div>
              )}
            </div>
            <p className="text-zinc-500 text-xs mt-3 text-center">
              {autoDetected
                ? 'Auto-detected the receipt — drag the corners to fine-tune'
                : 'Drag the corners to the edges of the receipt'}
            </p>
            <div className="flex gap-3 mt-4 w-full max-w-md max-md:gap-2">
              <button
                onClick={rotateCropImage}
                className="flex-1 py-3 bg-zinc-800 text-zinc-300 rounded-xl font-bold flex items-center justify-center gap-2 max-md:gap-1 max-md:text-sm"
                title="Rotate 90° — scanned it sideways?"
              >
                <RotateCw size={16} /> Rotate
              </button>
              <button onClick={onUseOriginal} className="flex-1 py-3 bg-zinc-800 text-zinc-300 rounded-xl font-bold flex items-center justify-center gap-2 max-md:gap-1 max-md:text-sm">
                <Maximize size={16} /> Full Image
              </button>
              <button
                onClick={() => {
                  const auto = detectDocumentQuad(workCanvasRef.current);
                  setAutoDetected(!!auto);
                  setCorners(auto || defaultCornersRef.current);
                }}
                className="flex-1 py-3 bg-zinc-800 text-zinc-300 rounded-xl font-bold flex items-center justify-center gap-2 max-md:gap-1 max-md:text-sm"
                title="Re-run auto-detect"
              >
                <RotateCcw size={16} /> Reset
              </button>
              <button onClick={doCrop} className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold flex items-center justify-center gap-2 max-md:gap-1 max-md:text-sm">
                <Crop size={16} /> Crop
              </button>
            </div>
          </>
        )}

        {stage === 'preview' && (
          <>
            <div className="w-full flex items-center justify-center bg-zinc-900/60 rounded-lg p-2">
              <canvas ref={previewCanvasRef} className="rounded block" style={{ maxWidth: '100%', maxHeight: '52vh', width: 'auto', height: 'auto' }} />
            </div>
            <div className="flex gap-3 mt-4 w-full max-w-md">
              <button onClick={() => setStage('crop')} className="flex-1 py-3 bg-zinc-800 text-zinc-300 rounded-xl font-bold flex items-center justify-center gap-2">
                <RotateCcw size={16} /> Re-crop
              </button>
              <button onClick={rotatePreview} className="flex-1 py-3 bg-zinc-800 text-zinc-300 rounded-xl font-bold flex items-center justify-center gap-2" title="Rotate 90°">
                <RotateCw size={16} /> Rotate
              </button>
              <button onClick={accept} className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-bold flex items-center justify-center gap-2">
                <Check size={16} /> Accept
              </button>
            </div>
          </>
        )}
      </div>
      </div>
    </div>
  );
}

// Safety net: if ANYTHING inside the cropper crashes, the user gets working
// escape buttons instead of a dead black screen. The photo is never lost —
// "Use Full Image" passes the original through uncropped.
class CropperBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { crashed: false };
  }
  static getDerivedStateFromError() {
    return { crashed: true };
  }
  componentDidCatch(err) {
    console.error('ReceiptCropper crashed:', err);
  }
  render() {
    if (this.state.crashed) {
      return (
        <div className="fixed inset-0 z-[130] bg-black/90 flex items-center justify-center p-6">
          <div className="text-center space-y-5 max-w-sm">
            <p className="text-red-400 font-medium">The crop tool hit a snag on this photo.</p>
            <button
              onClick={this.props.onUseOriginal}
              className="w-full py-3 bg-indigo-600 rounded-xl font-bold text-white"
            >
              Use Full Image Instead
            </button>
            <button
              onClick={this.props.onCancelAll}
              className="w-full py-3 bg-zinc-800 rounded-xl font-bold text-zinc-300"
            >
              Cancel
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function SafeReceiptCropper(props) {
  return (
    <CropperBoundary key={props.file?.name || 'crop'} onUseOriginal={props.onUseOriginal} onCancelAll={props.onCancelAll}>
      <ReceiptCropper {...props} />
    </CropperBoundary>
  );
}
